import React, { useMemo } from 'react';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import { useGetShippingCompany } from '@common/constants';
import {
  Gray_Light_100,
  Gray_Light_150,
  Gray_Light_200,
  Gray_Light_300,
  Gray_Light_500,
  Gray_Light_900,
  Pink_Light_400,
  Typography,
} from '@common/styles';
import { displayPrice } from '@common/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { PdpShippingFeeType, PdpShopShippingCompany, ShippingEstimateInfo } from '@domains/pdp-common';

export interface ShippingDetails {
  shipping_fee: number | null;
  shipping_fee_type: PdpShippingFeeType;
  free_shipping_amount: number | null;
  shipping_company: PdpShopShippingCompany | null;
  additional_shipping_fee_text: string | null;
}

export interface GeneralExtraInfoProps {
  shipping_estimate_info: ShippingEstimateInfo[] | null;
  shipping_details: ShippingDetails;
}

export const GeneralExtraInfo: React.FC<React.PropsWithChildren<GeneralExtraInfoProps>> = ({
  shipping_estimate_info,
  shipping_details,
}) => {
  const { shipping_company_name } = useGetShippingCompany(shipping_details.shipping_company ?? '');

  const highestRateDay = useMemo(() => {
    if (!shipping_estimate_info) {
      return 999;
    }
    const sortedByProbability = shipping_estimate_info.slice().sort((a, b) => b.probability - a.probability);
    return sortedByProbability[0].day;
  }, [shipping_estimate_info]);

  const feeToHumanFriendly = (amount: number | null) => {
    if (amount === null) {
      return '정보없음';
    }
    return amount > 0 ? displayPrice(amount) : '무료배송';
  };

  const getShippingAmount = (amount: number | null) => {
    if (shipping_details.shipping_fee_type === PdpShippingFeeType.FREE) {
      return '무료배송';
    }
    return feeToHumanFriendly(amount);
  };

  return (
    <>
      {shipping_estimate_info && (
        <Group>
          <GroupTitle className={Typography.BODY_15_MEDIUM}>배송 준비 기간</GroupTitle>
          {shipping_estimate_info.map(({ day, probability }, i, { length }) => {
            const isHighest = highestRateDay === day;
            const probabilityOfPercentage = Math.floor(probability * 100);
            return (
              <EstimateByDay isHighlight={isHighest} key={day}>
                <div className={`day ${Typography.BODY_14_REGULAR}`}>
                  {day}일
                  {length - 1 > i ? '' : '+'}
                </div>
                <svg className='graph'>
                  <rect width={`${probabilityOfPercentage}%`} height={4} />
                </svg>
                <div className={`probability ${Typography.BODY_14_REGULAR}`}>{probabilityOfPercentage}%</div>
              </EstimateByDay>
            );
          })}
          <SubMessage className={Typography.BODY_13_SEMIBOLD}>
            이 상품의 최근 한 달간 배송 준비 기간을 기반으로 한 확률입니다.<br />쇼핑몰 상황에 따라 안내된 기간보다 배송이 늦어질 수 있습니다.
          </SubMessage>
        </Group>
      )}
      {shipping_details && (
        <Group>
          <GroupTitle className={Typography.BODY_15_MEDIUM}>
            택배사 / 배송비
          </GroupTitle>
          <ShippingInfoRow className={Typography.BODY_14_REGULAR}>
            <LabelCol>택배사</LabelCol>
            <ValueCol>{shipping_company_name}</ValueCol>
          </ShippingInfoRow>
          <ShippingInfoRow className={Typography.BODY_14_REGULAR}>
            <LabelCol>배송비</LabelCol>
            <ValueCol>
              {getShippingAmount(shipping_details.shipping_fee)}
              {!!shipping_details.free_shipping_amount &&
                shipping_details.free_shipping_amount > 0 &&
                shipping_details.shipping_fee_type !== PdpShippingFeeType.FREE && (
                  <FreeShippingAmount>
                    &nbsp;∙&nbsp;
                    {feeToHumanFriendly(shipping_details.free_shipping_amount)} 이상 구매 시 무료
                  </FreeShippingAmount>
                )}
            </ValueCol>
          </ShippingInfoRow>
          {shipping_details.additional_shipping_fee_text && (
            <ShippingInfoRow className={Typography.BODY_14_REGULAR}>
              <LabelCol>추가 배송비</LabelCol>
              <ValueCol dangerouslySetInnerHTML={{ __html: shipping_details.additional_shipping_fee_text }} />
            </ShippingInfoRow>
          )}
          <GuideText>발송일이 다른 상품을 함께 구매할 경우 가장 늦은 발송일을 기준으로 배송됩니다.</GuideText>
        </Group>
      )}
    </>
  );
};

const shippingGraphFill = keyframes`
  from {
    width: 0;
  }
`;

const Group = styled.div`
  margin-bottom: 16px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const GroupTitle = styled.h2`
  padding: 8px 0 24px;
`;

const SubMessage = styled.p`
  margin: 16px 0;
  color: ${Gray_Light_300};
`;

const ShippingInfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const LabelCol = styled.span`
  width: 96px;
  color: ${Gray_Light_500};
`;

const ValueCol = styled.span`
  width: calc(100% - 96px);
  color: ${Gray_Light_900};
`;

const FreeShippingAmount = styled.span`
  color: ${Gray_Light_300};
`;

const GuideText = styled.p`
  padding-top: 16px;
  font-size: 14px;
  line-height: 140%;
  color: ${Gray_Light_500};
  border-top: 1px solid ${Gray_Light_100};
`;

const EstimateByDay = styled.div<{ isHighlight?: boolean }>`
  display: flex;
  align-items: center;
  height: 32px;

  ${(isHighlight) =>
    isHighlight &&
    css`
      .day,
      .probability {
        color: ${Pink_Light_400};
      }

      .graph rect {
        fill: ${Pink_Light_400};
      }
    `}

  .day {
    max-width: 105px;
    width: 100%;
    text-align: left;
  }

  .graph {
    min-width: 160px;
    height: 4px;
    overflow: hidden;
    border-radius: 2px;
    background: ${Gray_Light_150};
    rect {
      fill: ${Gray_Light_200};
      animation: shippingGraphFill 0.3s ease-in-out;
    }
  }

  .probability {
    max-width: 78px;
    width: 100%;
    text-align: right;
  }
`;
